import {
  AssetLifecycleSchema,
  CookieSchema,
  KeyValueSchema,
  PageScreenshotSchema,
  WebPageSchema,
  WebpageParameterSchema,
} from '@terra/shared-schemas-orchestrator';
import { z } from 'zod';
import { uploadedFileSchema } from '@terra/shared-authorization';
import { MongoObjectIdSchema } from '@terra/shared-utils';
import {
  FEATURE_RELATED_REQUESTS_DEFAULT_LIMIT,
  FEATURE_RELATED_REQUESTS_MAX_LIMIT,
  WEBPAGE_SCREENSHOT_MAX_SIZE_BYTES,
  WEBPAGE_SCREENSHOT_MIME_TYPES,
} from './web-pages.constants';

export const WebPagesParamsSchema = z.object({
  applicationId: z.string().describe('The application id'),
});

export const WebPagesQuerySchema = z.object({
  url: z
    .string()
    .optional()
    .describe('Filter webpages by URL (case-insensitive partial match)'),
  createdStartDate: z.coerce
    .date()
    .optional()
    .describe('Filter webpages created on or after this date'),
  createdEndDate: z.coerce
    .date()
    .optional()
    .describe('Filter webpages created on or before this date'),
  updatedStartDate: z.coerce
    .date()
    .optional()
    .describe('Filter webpages updated on or after this date'),
  updatedEndDate: z.coerce
    .date()
    .optional()
    .describe('Filter webpages updated on or before this date'),
  isPubliclyAccessible: z
    .boolean()
    .optional()
    .describe('Filter by public accessibility status'),
  limit: z.coerce
    .number()
    .int()
    .positive()
    .optional()
    .describe('Maximum number of webpages to return'),
  skip: z.coerce
    .number()
    .int()
    .nonnegative()
    .optional()
    .describe('Number of webpages to skip (for pagination)'),
  crawlerSessionId: z
    .string()
    .regex(/^[a-f\d]{24}$/i, 'Invalid crawler session id')
    .optional()
    .describe(
      'When set, return only webpages newly discovered in this crawler session',
    ),
});

const BaseWebPageCreateSchema = WebPageSchema.omit({
  tenantId: true,
  id: true,
  createdAt: true,
  updatedAt: true,
  hash: true,
});

export const WebPageReturnSchema = BaseWebPageCreateSchema.extend({
  id: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  screenshot: PageScreenshotSchema.optional(),
  role: z.string().optional(),
  isPubliclyAccessible: z.boolean().nullable().optional(),
  linksInternal: z.array(z.string()).optional(),
  linksExternal: z.array(z.string()).optional(),
  headers: z.array(KeyValueSchema).optional(),
  cookies: z.array(CookieSchema).optional(),
  lifecycle: AssetLifecycleSchema.optional(),
});

export const WebPageWithSignedScreenshotReturnSchema = WebPageReturnSchema.omit(
  {
    screenshot: true,
  },
).extend({
  signedUrl: z
    .string()
    .nullable()
    .describe('The signed url for the screenshot'),
  role: z
    .string()
    .optional()
    .describe('The role of the user visited the web page'),
  isPubliclyAccessible: z
    .boolean()
    .nullable()
    .optional()
    .describe('Whether the webpage is publicly accessible'),
  linksInternal: z.array(z.string()).optional(),
  linksExternal: z.array(z.string()).optional(),
  headers: z.array(KeyValueSchema).optional(),
  cookies: z.array(CookieSchema).optional(),
});

export const CreateWebPageSchema = BaseWebPageCreateSchema.extend({
  screenshot: PageScreenshotSchema.optional(),
  parameters: z.array(WebpageParameterSchema),
  cookies: z.array(CookieSchema).optional(),
  headers: z.array(KeyValueSchema).optional(),
  sources: WebPageSchema.shape.sources.default([]),
});

export const UpdateWebPageSchema = BaseWebPageCreateSchema.partial().extend({
  //
});

export const WebPageIdParamsSchema = z.object({
  id: z.string().describe('The web page id'),
});

export const WebpageRelatedEndpointSchema = z.object({
  id: z.string(),
  url: z.string(),
  path: z.string(),
  host: z.string(),
  description: z.string().nullable().optional(),
  request: z.object({
    method: z.enum(['GET', 'POST', 'DELETE', 'PUT', 'PATCH']),
  }),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
});

export const WebpageEndpointsQuerySchema = z.object({
  skip: z.coerce
    .number()
    .int()
    .nonnegative()
    .optional()
    .describe('Number of related endpoints to skip for pagination'),
  limit: z.coerce
    .number()
    .int()
    .positive()
    .optional()
    .describe('Maximum number of related endpoints to return'),
});

export const WebpageEndpointsReturnSchema = z.object({
  endpoints: z.array(WebpageRelatedEndpointSchema),
  totalCount: z
    .number()
    .int()
    .nonnegative()
    .describe('Total number of endpoints related to this webpage'),
});

export const ErrorResponseSchema = z.object({
  message: z.string(),
});

export const DeleteResponseSchema = z.object({
  message: z.string(),
});

export const WebpageFeatureSchema = z.object({
  featureType: z
    .enum(['get', 'create', 'update', 'delete'])
    .describe('The CRUD type of the feature'),
  description: z
    .string()
    .describe(
      'Description of the feature - whats the feature about and what it meant to do',
    ),
  reproduce: z
    .string()
    .describe(
      'Step-by-step instructions for a human to reproduce/trigger this feature. Should describe how to locate and interact with the feature through the page UI, without specific test data. Put each step on its own line, separated by a newline, in the format "N. step" (for example "1. Open the page.\\n2. Click Save.").',
    ),
  harEntryIds: z
    .array(z.string())
    .optional()
    .describe('IDs of HAR entries correlated to this feature'),
  executionPolicy: z
    .enum(['execute', 'block'])
    .optional()
    .describe('Whether FA should execute this feature after discovery'),
  executionBlockLabels: z
    .array(
      z.enum([
        'CROSS_USER',
        'CROSS_ENTITY',
        'AUTHORIZATION',
        'ROLE',
        'PERMISSIONS',
        'SESSION',
        'ENROLLMENT',
        'CREDENTIAL',
        'IMPERSONATION',
        'MEMBERSHIP',
        'OWNERSHIP',
        'ACCOUNT_STATUS',
        'PAYMENT',
        'SECRET',
        'NOTIFY',
        'FILE_UPLOAD',
        'DATA_EXPORT',
        'EXTERNAL_INTEGRATION',
        'UNCLASSIFIED',
      ]),
    )
    .optional()
    .describe(
      'Atomic composable guardrail labels when executionPolicy is block (multiple may apply)',
    ),
  matchedGuardrailIds: z.array(z.string()).optional(),
  executionBlockReason: z.string().optional(),
});

export const FeatureRelatedRequestSchema = z.object({
  id: z.string(),
  method: z.string(),
  url: z.string(),
  status: z.number().int().nullable(),
  endpointId: z.string().nullable(),
  webpageId: z.string().nullable(),
  requestHeaders: z.record(
    z.string(),
    z.union([z.string(), z.array(z.string())]),
  ),
  requestBody: z.string().nullable(),
  httpVersion: z.string().nullable(),
});

export const FeatureRelatedRequestsParamsSchema = z.object({
  id: z.string().describe('The web page ID'),
  featureId: z.string().describe('The web page feature ID'),
});

export const FeatureRelatedRequestsQuerySchema = z.object({
  skip: z.coerce
    .number()
    .int()
    .nonnegative()
    .optional()
    .describe('Number of related requests to skip for pagination'),
  limit: z.coerce
    .number()
    .int()
    .positive()
    .max(FEATURE_RELATED_REQUESTS_MAX_LIMIT)
    .default(FEATURE_RELATED_REQUESTS_DEFAULT_LIMIT),
});

export const FeatureRelatedRequestsResponseSchema = z.object({
  relatedRequests: z.array(FeatureRelatedRequestSchema),
  totalCount: z.number().int().nonnegative(),
});

export const ExecuteFeatureOverrideParamsSchema = z.object({
  id: MongoObjectIdSchema.describe('The web page ID'),
  featureId: MongoObjectIdSchema.describe('The web page feature ID'),
});

export const ExecuteFeatureOverrideBodySchema = z.object({
  overridePolicy: z.boolean(),
  providedExecutionContext: z.string().max(4000).optional(),
  forceRerun: z.boolean().optional(),
});

export const ExecuteFeatureOverrideResponseSchema = z.object({
  featureExecutionRunId: z.string().uuid(),
});

export const WebPageFeatureAuditEventSchema = z.object({
  kind: z.enum([
    'blocked',
    'unblocked',
    'reproduced',
    'reproduce_failed',
    'mapping_failed',
  ]),
  occurredAt: z.coerce.date(),
  actorName: z.string().optional(),
  actorPicture: z.string().optional(),
  isTerraUser: z.boolean().optional(),
});

export const WebPageFeatureReturnSchema = WebpageFeatureSchema.extend({
  id: z.string(),
  reproduce: z.string().nullable(),
  executionPolicy: z.enum(['execute', 'block']).nullable(),
  executionBlockLabels: WebpageFeatureSchema.shape.executionBlockLabels
    .unwrap()
    .default([]),
  matchedGuardrailIds: z.array(z.string()).optional(),
  executionBlockReason: z.string().nullable().optional(),
  harEntryIds: z.array(z.string()).default([]),
  isFeatureExecuted: z.boolean(),
  featureScreenshotUrl: z
    .string()
    .nullable()
    .describe('Signed URL for the feature-specific screenshot'),
  lastRunIsPolicyOverride: z.boolean(),
  latestCrawlerSessionStatus: z
    .enum(['RUNNING', 'ABORTING', 'COMPLETED', 'FAILED', 'ABORTED'])
    .nullable(),
  auditTrail: WebPageFeatureAuditEventSchema.array(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export const GetWebPageFeaturesResponseSchema = z.object({
  features: WebPageFeatureReturnSchema.array(),
});

export const TriggerUpdateWebpagesBodySchema = z.object({
  applicationId: z.string().describe('The application ID'),
  credentialId: z
    .string()
    .nullable()
    .describe('Credential ID (null for anonymous)'),
  recreateAll: z
    .boolean()
    .optional()
    .default(false)
    .describe('If true, recreate all webpages from scratch'),
});

export const TriggerUpdateWebpagesResponseSchema = z.object({
  success: z.boolean().describe('Whether the trigger was successful'),
});

export const CountUntrackedPagesParamsSchema = z.object({
  applicationId: z.string().describe('The application ID'),
  credentialId: z
    .string()
    .nullable()
    .describe('Credential ID (null for anonymous)'),
});

export const CountUntrackedPagesResponseSchema = z.object({
  count: z.int().nonnegative().describe('Number of untracked pages'),
});

export const CountTotalPagesParamsSchema = z.object({
  applicationId: z.string().describe('The application ID'),
  credentialId: z
    .string()
    .nullable()
    .describe('Credential ID (null for anonymous)'),
});

export const CountTotalPagesResponseSchema = z.object({
  count: z.int().nonnegative().describe('Total number of pages'),
});

export const EnrichWebpagesBodySchema = z.object({
  applicationId: z.string().describe('The application ID'),
  webpageIds: z
    .array(z.string())
    .optional()
    .describe(
      'Optional list of webpage IDs to enrich. If not provided, all webpages with 0 cookies, 0 links, and 0 headers will be enriched',
    ),
});

export const EnrichWebpagesResponseSchema = z.object({
  enrichedCount: z
    .int()
    .nonnegative()
    .describe('Number of webpages successfully enriched'),
  failedCount: z
    .int()
    .nonnegative()
    .describe('Number of webpages that failed to enrich'),
  totalProcessed: z
    .int()
    .nonnegative()
    .describe('Total number of webpages processed'),
});

export const TriggerFeatureAnalysisBodySchema = z.object({
  applicationId: z.string().describe('The application ID'),
  webpageIds: z
    .tuple([z.string()], z.string())
    .optional()
    .describe(
      'Optional list of webpage IDs to analyze. If omitted, all webpages for the application are analyzed.',
    ),
  force: z
    .boolean()
    .optional()
    .describe(
      'Bypass completed, running (after aborting prior session), and max-attempt skips; with executionOnly, re-enqueue already-executed features',
    ),
  executionOnly: z
    .boolean()
    .optional()
    .describe(
      'Enqueue FEATURE_EXECUTION jobs for existing DB features (no page discovery). CLI alias: --trigger-features',
    ),
});

export const FeatureAnalysisTriggerSkipReasonSchema = z.enum([
  'SKIP_COMPLETED',
  'SKIP_RUNNING',
  'SKIP_MAX_ATTEMPTS',
  'NO_FEATURES',
  'NO_ROOT_SESSION',
]);

export const TriggerFeatureAnalysisResponseSchema = z.object({
  message: z.string().describe('Confirmation message'),
  accepted: z
    .array(z.string())
    .describe('Webpage IDs accepted for discovery or execution enqueue'),
  skipped: z
    .array(
      z.object({
        webpageId: z.string(),
        reason: FeatureAnalysisTriggerSkipReasonSchema,
      }),
    )
    .describe('Webpage IDs skipped with eligibility or enqueue reasons'),
  executionsEnqueued: z
    .int()
    .nonnegative()
    .optional()
    .describe('Feature execution jobs published when executionOnly is true'),
});

export const GenerateWebpageDescriptionParamsSchema = z.object({
  webpageId: z
    .string()
    .describe('The webpage ID to generate a description for'),
});

export const GenerateWebpageDescriptionResponseSchema = z.object({
  webpageId: z.string().describe('The webpage ID'),
  description: z
    .string()
    .nullable()
    .describe(
      'The generated description, or null if no screenshot was available',
    ),
});

export const ReplaceWebpageScreenshotBodySchema = z.object({
  screenshot: uploadedFileSchema({
    mimeTypes: WEBPAGE_SCREENSHOT_MIME_TYPES,
    maxFileSizeBytes: WEBPAGE_SCREENSHOT_MAX_SIZE_BYTES,
  }),
});
